import type { IEpochStore } from "@/domains/epochs/interfaces"
import {
  formatEpochDate,
  formatEpochInteger,
  formatEpochPercentage,
  formatEpochTimestamp,
} from "@/domains/epochs/utils/epoch-formatters"
import { Compound as Table } from "@/components/table"
import useResponsive from "@/hooks/use-responsive"

import { RecycledEmissionsChart } from "./recycled-emissions-chart"
import { CurrentEpochSidePanel } from "./current-epoch-side-panel"

export interface EpochsViewProps {
  currentEpoch: IEpochStore | undefined
  historicEpochs: IEpochStore[]
  hoveredEpoch: number | null
  setHoveredEpoch: (epoch: number | null) => void
  mousePosition: { x: number; y: number } | null
  setMousePosition: (position: { x: number; y: number } | null) => void
  showAllEpochs: boolean
  onToggleAllEpochs: (checked: boolean) => void
}

export function EpochsView({
  currentEpoch,
  historicEpochs,
  hoveredEpoch,
  setHoveredEpoch,
  mousePosition,
  setMousePosition,
  showAllEpochs,
  onToggleAllEpochs,
}: EpochsViewProps) {
  // Sort historic epochs by epochNumber descending (newest first)
  const sortedHistoricEpochs = [...historicEpochs].sort((a, b) => b.epochNumber - a.epochNumber)

  const { isDesktop } = useResponsive()

  return (
    <div className="flex flex-col gap-8">
      {/* Mobile/Tablet: Stacked layout */}
      {!isDesktop && (
        <>
          {/* Current Epoch Display */}
          <div className="h-fit w-full rounded-lg border border-p400-light bg-p400 px-8 py-8 text-t100">
            <h1 className="mb-1 text-xl font-bold capitalize md:text-2xl">Current Epoch</h1>
            <p className="mb-8 text-lg text-t200">Latest epoch data and metrics</p>

            {currentEpoch ? (
              <div className="flex flex-col gap-4">
                <div className="text-3xl font-bold text-t100">Epoch {currentEpoch.epochNumber}</div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <div className="flex flex-col gap-1">
                <div className="text-sm text-t200">Recycled</div>
                <div className="text-lg font-semibold text-t100">{formatEpochPercentage(currentEpoch.recycledPercentage)}</div>
                
                <div className="text-base font-medium text-t200">{formatEpochInteger(currentEpoch.recycled)}</div>
              </div>
              <div className="flex flex-col gap-1">
                <div className="text-sm text-t200">Fresh</div>
                <div className="text-lg font-semibold text-t100">{formatEpochPercentage(currentEpoch.freshPercentage)}</div>
                
                <div className="text-base font-medium text-t200">{formatEpochInteger(currentEpoch.mints)}</div>
              </div>
              <div className="flex flex-col gap-1">
                <div className="text-sm text-t200">Total Emitted</div>
                <div className="text-lg font-semibold text-t100">100%</div>
                
                <div className="text-base font-medium text-t200">{formatEpochInteger(currentEpoch.gaugesMint)}</div>
              </div>
              <div className="flex flex-col gap-1">
                <div className="text-sm text-t200">Date</div>
                <div className="text-lg font-semibold text-t100">{formatEpochDate(currentEpoch.startTimestamp)}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex h-full min-h-[200px] w-full items-center justify-center">
                <div className="loading loading-spinner loading-lg text-sm text-t200">Loading current epoch..</div>
              </div>
            )}
          </div>

          {/* Emissions Chart */}
          <div className="h-fit w-full rounded-lg border border-p400-light bg-p400 px-8 py-8 text-t100">
            <h1 className="mb-1 text-xl font-bold capitalize md:text-2xl">Emissions Chart</h1>
            <p className="mb-8 text-lg text-t200">Visualization of epoch emissions data</p>
            <RecycledEmissionsChart
              historicEpochs={historicEpochs}
              currentEpoch={currentEpoch}
              hoveredEpoch={hoveredEpoch}
              setHoveredEpoch={setHoveredEpoch}
              mousePosition={mousePosition}
              setMousePosition={setMousePosition}
            />
          </div>
        </>
      )}

      {/* Desktop: Side-by-side layout */}
      {isDesktop && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_minmax(auto,400px)] items-stretch">
          {/* Emissions Chart - Left side */}
          <div className="flex h-full w-full flex-col rounded-lg border border-p400-light bg-p400 px-8 py-8 text-t100">
            <h1 className="mb-1 text-xl font-bold capitalize md:text-2xl">Emissions Chart</h1>
            <p className="mb-8 text-lg text-t200">Visualization of epoch emissions data</p>
            <div className="flex-1">
              <RecycledEmissionsChart
                historicEpochs={historicEpochs}
                currentEpoch={currentEpoch}
                hoveredEpoch={hoveredEpoch}
                setHoveredEpoch={setHoveredEpoch}
                mousePosition={mousePosition}
                setMousePosition={setMousePosition}
              />
            </div>
          </div>

          {/* Current Epoch Side Panel - Right side */}
          <div className="flex h-full flex-col">
            <CurrentEpochSidePanel currentEpoch={currentEpoch} />
          </div>
        </div>
      )}

      {/* Historic Epochs Table */}
      <div className="h-fit w-full rounded-lg border border-p400-light bg-p400 px-8 py-8 text-t100">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="mb-1 text-xl font-bold capitalize md:text-2xl">Historic Epochs</h1>
            <p className="text-lg text-t200">
              {showAllEpochs ? "Historical epoch data from epoch 0 onwards" : "Historical epoch data from epoch 100 onwards"}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-t300">
            <button
              onClick={() => onToggleAllEpochs(!showAllEpochs)}
              className={`relative h-5 w-9 cursor-pointer rounded-full p-0.5 transition-colors ${
                showAllEpochs ? "bg-primary-400" : "bg-p400 hover:bg-p400-light"
              }`}
              aria-label="Toggle All Epochs"
            >
              <div
                className={`h-4 w-4 rounded-full shadow-sm transition-transform ${
                  showAllEpochs ? "translate-x-4 bg-white" : "translate-x-0 bg-p200"
                }`}
              />
            </button>
            <span>All Epochs</span>
          </div>
        </div>

        {sortedHistoricEpochs.length > 0 ? (
          <Table.Root>
            <Table.Head>
              <Table.HeaderRow>
                <Table.Th className="text-center">
                  <div className="flex flex-col items-center justify-center">DATE</div>
                </Table.Th>
                <Table.Th className="text-center">
                  <div className="flex w-full flex-col items-center justify-center">EPOCH</div>
                </Table.Th>
                <Table.Th className="text-center">
                  <div className="flex w-full flex-col items-center justify-center">FRESH</div>
                </Table.Th>
                <Table.Th className="text-center">
                  <div className="flex w-full flex-col items-center justify-center">FRESH %</div>
                </Table.Th>
                <Table.Th className="text-center">
                  <div className="flex w-full flex-col items-center justify-center">RECYCLED</div>
                </Table.Th>
                <Table.Th className="text-center">
                  <div className="flex w-full flex-col items-center justify-center">RECYCLED %</div>
                </Table.Th>
                <Table.Th className="text-center">
                  <div className="flex w-full flex-col items-center justify-center">
                    <span>TOTAL</span>
                    <span>EMITTED</span>
                  </div>
                </Table.Th>
                <Table.Th className="text-center">
                  <div className="flex w-full flex-col items-center justify-center">
                    <span>EFFECTIVE</span>
                    <span>TOTAL</span>
                  </div>
                </Table.Th>
              </Table.HeaderRow>
            </Table.Head>
            <Table.Body>
              {sortedHistoricEpochs.map((epoch) => {
                // Add hover highlighting
                const isHovered = hoveredEpoch === epoch.epochNumber
                const rowClassName = isHovered ? "bg-p300 text-t100" : ""

                return (
                  <Table.Tr key={epoch.epochNumber} className={rowClassName}>
                    <Table.Td>{formatEpochDate(epoch.startTimestamp)}</Table.Td>
                    <Table.Td className="text-center font-semibold">{epoch.epochNumber}</Table.Td>
                    <Table.Td className="text-center">{formatEpochInteger(epoch.mints)}</Table.Td>
                    <Table.Td className="text-center">{formatEpochPercentage(epoch.freshPercentage)}</Table.Td>
                    <Table.Td className="text-center">{formatEpochInteger(epoch.recycled)}</Table.Td>
                    <Table.Td className="text-center">{formatEpochPercentage(epoch.recycledPercentage)}</Table.Td>
                    <Table.Td className="text-center">{formatEpochInteger(epoch.gaugesMint)}</Table.Td>
                    <Table.Td className="text-center">{formatEpochInteger(epoch.gaugesMint)}</Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Body>
          </Table.Root>
        ) : (
          <div className="flex h-full min-h-[200px] w-full items-center justify-center">
            <div className="text-t200">No historic epochs data available</div>
          </div>
        )}
      </div>
    </div>
  )
}
